import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, Container, Typography, Button, AppBar, Toolbar } from '@mui/material';
import { Home as HomeIcon } from '@mui/icons-material';
import {
  apiUrl,
  apiFetch,
  getHeaders,
  setOnSessionExpired,
  clearStoredTokens,
} from './config/api';
import { theme } from './theme';

import NezoolaAdminDashboard from './components/NezoolaAdminDashboard';
import LoginForm from './components/LoginForm';

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);
  const [userRole, setUserRole] = React.useState(null);
  const [token, setToken] = React.useState(localStorage.getItem('token'));

  const checkAuthStatus = React.useCallback(async () => {
    try {
      const response = await apiFetch(apiUrl('/auth/profile'), {
        headers: getHeaders(token),
      });

      if (response.ok) {
        const data = await response.json();
        setIsAuthenticated(true);
        setUserRole(data.data.user.role);
      } else {
        clearStoredTokens();
        setToken(null);
        setIsAuthenticated(false);
        setUserRole(null);
      }
    } catch (error) {
      clearStoredTokens();
      setToken(null);
      setIsAuthenticated(false);
      setUserRole(null);
    }
  }, [token]);

  React.useEffect(() => {
    if (token) {

      checkAuthStatus();
    }
  }, [token, checkAuthStatus]);

  React.useEffect(() => {
    if (isAuthenticated && userRole === 'admin' && location.pathname === '/') {
      navigate('/admin');
    }
  }, [isAuthenticated, userRole, navigate, location.pathname]);

  const handleLogin = (userData, userToken) => {
    if (!userData || userData.role !== 'admin') {
      setToken(null);
      setUserRole(null);
      setIsAuthenticated(false);
      clearStoredTokens();
      return;
    }

    setToken(userToken);
    setUserRole(userData.role);
    setIsAuthenticated(true);
    localStorage.setItem('token', userToken);
    navigate('/admin');
  };

  const handleLogout = () => {
    setToken(null);
    setUserRole(null);
    setIsAuthenticated(false);
    clearStoredTokens();
    navigate('/');
  };

  React.useEffect(() => {
    setOnSessionExpired(() => {
      clearStoredTokens();
      setToken(null);
      setUserRole(null);
      setIsAuthenticated(false);
      navigate('/');
    });
    return () => setOnSessionExpired(null);
  }, [navigate]);

  const HomePage = () => {
    return (
      <Box sx={{
        minHeight: 'calc(100vh - 64px)',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 8
      }}>
        <Box sx={{
          position: 'absolute',
          top: -50,
          right: -50,
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.1)',
          filter: 'blur(40px)'
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: -50,
          left: -50,
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.1)',
          filter: 'blur(40px)'
        }} />
        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
          {!isAuthenticated && (
            <Box sx={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              borderRadius: 4,
              boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
              p: 4
            }}>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography variant="h3" component="h1" sx={{
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1
                }}>
                  Nezoola Dashboard
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Welcome back! Please login to continue
                </Typography>
              </Box>
              <LoginForm onLogin={handleLogin} />
            </Box>
          )}

          {isAuthenticated && (
            <Box sx={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              borderRadius: 4,
              boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
              p: 4,
              textAlign: 'center'
            }}>
              <Typography variant="h4" component="h1" sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2
              }}>
                Welcome back!
              </Typography>
              <Typography variant="h6" gutterBottom color="text.secondary" sx={{ mb: 3 }}>
                You're logged in as: <strong>{userRole}</strong>
              </Typography>
              <Button
                variant="contained"
                onClick={handleLogout}
                sx={{
                  background: 'linear-gradient(135deg, #f5576c 0%, #f093fb 100%)',
                  boxShadow: '0 4px 15px rgba(245, 87, 108, 0.4)',
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                  px: 4,
                  py: 1.5,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    boxShadow: '0 6px 20px rgba(245, 87, 108, 0.5)'
                  }
                }}
              >
                Logout
              </Button>
            </Box>
          )}
        </Container>
      </Box>
    );
  };

  return (
    <>
      <AppBar
        position="static"
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box sx={{
          position: 'absolute',
          top: -20,
          right: -20,
          width: 100,
          height: 100,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.1)',
          filter: 'blur(20px)'
        }} />
        <Toolbar sx={{ position: 'relative', zIndex: 1 }}>
          <HomeIcon sx={{ mr: 2, fontSize: 28 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 700, fontSize: '1.25rem' }}>
            Nezoola Dashboard
          </Typography>
          {isAuthenticated && (
            <>
              <Button
                color="inherit"
                onClick={() => navigate('/')}
                sx={{
                  mr: 1,
                  fontWeight: 500,
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                HOME
              </Button>
              {userRole === 'admin' && (
                <Button
                  color="inherit"
                  onClick={() => navigate('/admin')}
                  sx={{
                    mr: 1,
                    fontWeight: 500,
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                >
                  ADMIN DASHBOARD
                </Button>
              )}
              <Button
                color="inherit"
                onClick={handleLogout}
                sx={{
                  fontWeight: 500,
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                LOGOUT
              </Button>
            </>
          )}
        </Toolbar>
      </AppBar>

        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route
            path="/admin"
            element={
              isAuthenticated && userRole === 'admin' ? (
                <NezoolaAdminDashboard token={token} userRole={userRole} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route path="/test" element={<div>Test Route Working!</div>} />
        </Routes>
    </>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AppContent />
      </Router>
    </ThemeProvider>
  );
}

export default App;
